import {
  MAX_REPORTED_RELATIONS,
  MAX_SHAPE_DEPTH,
  MAX_SHAPE_NODES,
} from "./constants";
import type { IncludeShape, IncludeShapeOptions } from "./types";

/**
 * Derive the fan-out shape a query *asked for*, from its arguments alone.
 *
 * This is the cheap half of the instrument. It is O(args) — proportional to
 * how elaborate the query is, not to how much it returned — so it can run on a
 * far cheaper trigger than the result walk, and it still flags a dangerous
 * query shape on the run where it happens to return few rows.
 *
 * Returns `undefined` when the query asks for no relation hydration at all,
 * which is also the signal the extension uses to decide a result walk isn't
 * worth doing.
 *
 * PHI: only `include` and `select` are ever descended into (see
 * `TRAVERSABLE_ARG_KEYS`), and only their KEYS are read. `where`, `data`,
 * `cursor` and `orderBy` are never touched.
 */
export const describeIncludeShape = (
  args: unknown,
  {
    maxDepth = MAX_SHAPE_DEPTH,
    maxNodes = MAX_SHAPE_NODES,
    maxReportedRelations = MAX_REPORTED_RELATIONS,
  }: IncludeShapeOptions = {},
): IncludeShape | undefined => {
  if (!_isPlainish(args)) {
    return undefined;
  }

  const relations: string[] = [];
  let relationCount = 0;
  let depth = 0;
  let truncated = false;
  let visited = 0;

  const queue: _Frame[] = [];
  _enqueueContainers(queue, args, "", 1);

  while (queue.length > 0) {
    // Non-null: guarded by the loop condition, and nothing else shifts.
    const frame = queue.shift() as _Frame;

    visited += 1;
    if (visited > maxNodes) {
      truncated = true;
      break;
    }

    for (const key of Object.keys(frame.container)) {
      const value = frame.container[key];
      const isNested = _isPlainish(value);

      // In `include`, every key is a relation. In `select`, a `true` is a
      // scalar column — only an object means another relation is hydrated.
      if (!isNested && !(frame.kind === "include" && value === true)) {
        continue;
      }

      const path = frame.path === "" ? key : `${frame.path}.${key}`;
      relationCount += 1;
      if (relations.length < maxReportedRelations) {
        relations.push(path);
      } else {
        truncated = true;
      }
      if (frame.depth > depth) {
        depth = frame.depth;
      }

      if (!isNested) {
        continue;
      }
      if (frame.depth >= maxDepth) {
        truncated = true;
        continue;
      }
      _enqueueContainers(queue, value, path, frame.depth + 1);
    }
  }

  if (relationCount === 0) {
    return undefined;
  }

  return { depth, relationCount, relations, truncated };
};

type _Frame = {
  container: Record<string, unknown>;
  kind: "include" | "select";
  path: string;
  depth: number;
};

const _isPlainish = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const _enqueueContainers = (
  queue: _Frame[],
  source: Record<string, unknown>,
  path: string,
  depth: number,
) => {
  const include = source.include;
  if (_isPlainish(include)) {
    queue.push({ container: include, kind: "include", path, depth });
  }

  const select = source.select;
  if (_isPlainish(select)) {
    queue.push({ container: select, kind: "select", path, depth });
  }
};
